import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, InputNumber, message } from 'antd';
import styled from 'styled-components';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { PatientInfoForm } from './types';
import { savePatientInfo } from '../../services/consultationService';

const Container = styled.div<{ isFullscreen?: boolean }>`
    background: #fff;
    border-radius: ${props => props.isFullscreen ? '0' : '8px'};
    padding: ${props => props.isFullscreen ? '0' : '0'};
    max-width: ${props => props.isFullscreen ? '100%' : '600px'};
    display: flex;
    flex-direction: column;
    ${props => props.isFullscreen && `
        height: 100vh;
        width: 100%;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    `}
`;

const Header = styled.div<{ isFullscreen?: boolean }>`
    display: flex;
    align-items: center;
    margin-bottom: ${props => props.isFullscreen ? '0' : '24px'};
    padding: ${props => props.isFullscreen ? '12px 24px' : '0'};
    flex-shrink: 0;
    ${props => props.isFullscreen && `
        background: #fff;
        border-bottom: 1px solid #f0f0f0;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 100;
    `}
    
    .back-btn {
        margin-right: 16px;
        border: none;
        padding: 4px;
        &:hover {
            background: #f5f5f5;
        }
    }

    .title {
        font-size: 14px;
        color: #1f1c2b;
    }
`;

const FormContent = styled.div<{ isFullscreen?: boolean }>`
    flex: 1;
    overflow-y: auto;
    padding: ${props => props.isFullscreen ? '16px 24px' : '0'};
    ${props => props.isFullscreen ? `
        margin-top: 56px;  // 为固定的头部预留空间
        margin-bottom: 76px;  // 为固定的底部按钮预留空间
        height: calc(100vh - 132px);  // 减去头部和底部的高度
    ` : `
        max-height: 70vh;  // 非全屏模式下限制最大高度
        overflow-y: auto;
    `}
    
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #ccc;
        border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
        background: #aaa;
    }

    .ant-form-item:last-child {
        margin-bottom: 16px;
    }
`;

const ButtonContainer = styled.div<{ isFullscreen?: boolean }>`
    padding: ${props => props.isFullscreen ? '12px 24px' : '0'};
    background: #fff;
    ${props => props.isFullscreen && `
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        border-top: 1px solid #f0f0f0;
        z-index: 100;
    `}

    .submit-button {
        height: 40px;
        font-size: 14px;
    }
`;

interface InfoProps {
    patientId?: string;
    accountId: string;
    templateId: string;
    doctorId: string;
    name?: string;
    gender?: string;
    age?: number;
    weight?: number;
    height?: number;
    maritalStatus?: boolean;
    contactWay?: string;
    onSubmit?: (values: PatientInfoForm) => void;
    onBack?: () => void;
    isFullscreen?: boolean;
}

const Info: React.FC<InfoProps> = ({
    patientId,
    accountId,
    templateId,
    doctorId,
    name,
    gender,
    age,
    weight,
    height,
    maritalStatus,
    contactWay,
    onSubmit,
    onBack,
    isFullscreen
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // 如果传入了就诊人信息，设置表单初始值
        if (name) {
            form.setFieldsValue({
                name,
                gender: gender === 'man' ? 'man' : 'woman',
                age,
                weight,
                height,
                maritalStatus,
                contactWay
            });
        }
    }, [name, gender, age, weight, height, maritalStatus, contactWay]);

    const handleSubmit = async (values: any) => {
        setLoading(true);
        try {
            const formData: PatientInfoForm = {
                accountId,
                templateId,
                doctorId,
                patientId: patientId || '',
                name: values.name,
                gender: values.gender === 'man' ? 'man' : 'women',
                age: values.age,
                weight: values.weight,
                height: values.height,
                maritalStatus: values.maritalStatus,
                contactWay: values.contactWay,
                symptomDescription: values.symptomDescription,
                supplementaryMaterials: values.supplementaryMaterials || '',
            };

            // 如果是新建就诊人（没有patientId），先调用保存接口
            if (!patientId) {
                try {
                    const response = await savePatientInfo(formData);
                    if (response?.data?.recordId) {
                        onSubmit?.(formData);
                    } else {
                        message.error('保存就诊人信息失败');
                    }
                } catch (error) {
                    console.error('保存就诊人信息失败:', error);
                    message.error('保存就诊人信息失败');
                }
            } else {
                // 如果是编辑就诊人，直接调用父组件的onSubmit
                onSubmit?.(formData);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container isFullscreen={isFullscreen}>
            <Header isFullscreen={isFullscreen}>
                <Button
                    className="back-btn"
                    icon={<ArrowLeftOutlined />}
                    onClick={onBack}
                />
                <div className="title">填写就诊人信息</div>
            </Header>

            <FormContent isFullscreen={isFullscreen}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    requiredMark="optional"
                    style={{ paddingRight: '10px' }}
                >
                    <Form.Item
                        label="姓名"
                        name="name"
                        rules={[{ required: true, message: '请输入姓名' }]}
                    >
                        <Input maxLength={10} placeholder="请输入姓名" />
                    </Form.Item>

                    <Form.Item
                        label="性别"
                        name="gender"
                        rules={[{ required: true, message: '请选择性别' }]}
                    >
                        <Select placeholder="请选择性别">
                            <Select.Option value="man">男</Select.Option>
                            <Select.Option value="woman">女</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="年龄"
                        name="age"
                        rules={[{ required: true, message: '请输入年龄' }]}
                    >
                        <InputNumber
                            min={1}
                            max={150}
                            placeholder="请输入年龄"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <Form.Item
                        label="体重"
                        name="weight"
                        rules={[{ required: true, message: '请输入体重' }]}
                    >
                        <InputNumber
                            min={1}
                            max={300}
                            placeholder="请输入体重(kg)"
                            style={{ width: '100%' }}
                            precision={1}
                        />
                    </Form.Item>

                    <Form.Item
                        label="主诉"
                        name="symptomDescription"
                        rules={[{ required: true, message: '请描述最近身体哪里很不舒服' }]}
                    >
                        <Input.TextArea
                            placeholder="请描述最近身体哪里很不舒服"
                            maxLength={200}
                            showCount
                            autoSize={{ minRows: 3 }}
                        />
                    </Form.Item>

                    <Form.Item
                        label="身高"
                        name="height"
                    >
                        <InputNumber
                            min={1}
                            max={300}
                            placeholder="请输入身高(cm)"
                            style={{ width: '100%' }}
                            precision={1}
                        />
                    </Form.Item>

                    <Form.Item
                        label="婚姻状况"
                        name="maritalStatus"
                        dependencies={['gender']}
                        rules={[
                            ({ getFieldValue }) => ({
                                required: getFieldValue('gender') === 'woman',
                                message: '女性患者请选择婚姻状况',
                            }),
                        ]}
                    >
                        <Select placeholder="请选择婚姻状况">
                            <Select.Option value={true}>已婚</Select.Option>
                            <Select.Option value={false}>未婚</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="联系电话"
                        name="contactWay"
                        rules={[
                            {
                                pattern: /^1[3-9]\d{9}$/,
                                message: '请输入正确的手机号码'
                            }
                        ]}
                    >
                        <Input placeholder="请输入手机号码" maxLength={11} />
                    </Form.Item>

                    <Form.Item
                        label="补充材料"
                        name="supplementaryMaterials"
                    >
                        <Input placeholder="请输入补充材料URL，多个用逗号分隔" />
                    </Form.Item>

                    <ButtonContainer isFullscreen={isFullscreen}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            block
                            loading={loading}
                            className="submit-button"
                        >
                            提交
                        </Button>
                    </ButtonContainer>
                </Form>
            </FormContent>
        </Container>
    );
};

export default Info; 